import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const initialState: OrderDomain.State = {
	checkout: {
		subtotal: 0,
		taxAmount: 0,
		total: 0,
		step: 1,
		pickupName: '',
		pickupLocation: 'IN_STORE',
		giftCardPayments: [],
		guestCheckout: false,
		car: undefined
	},
	typewriterNote: {
		itemId: '',
		note: ''
	},
	lastTransactionSent: false
};

const slice = createSlice({
	name: 'orderSlice',
	initialState: initialState,
	reducers: {
		sendTransaction: (
			state: OrderDomain.State,
			action: PayloadAction<OrderDomain.State>
		) => {
			const { order, checkout, typewriterNote } = action.payload;
			state.order = order;
			state.checkout = {
				...checkout,
				step: 3
			};
			state.typewriterNote = typewriterNote;
			state.lastTransactionSent = true;
		},
		loggedOut: () => {
			return initialState;
		},
		cartUpdated: (
			state: OrderDomain.State,
			action: PayloadAction<OrderDomain.State>
		) => {
			const { order, checkout, lastTransactionSent } = action.payload;
			state.order = order;
			state.checkout = {
				...checkout,
				step: 1
			};
			state.typewriterNote = {
				itemId: '',
				note: ''
			};
			state.lastTransactionSent = lastTransactionSent;
		},
		orderSent: (
			state: OrderDomain.State,
			action: PayloadAction<OrderDomain.State>
		) => {
			const { order, lastTransactionSent } = action.payload;
			state.order = order;
			state.checkout = {
				...initialState.checkout,
				guestCheckout: state.checkout.guestCheckout
			};
			state.lastTransactionSent = lastTransactionSent;
		},
		orderStatusUpdate: (
			state: OrderDomain.State,
			action: PayloadAction<OrderDomain.State>
		) => {
			const { order, checkout, typewriterNote, lastTransactionSent } =
				action.payload;

			state.order = order;
			state.checkout = checkout;
			state.typewriterNote = typewriterNote;
			state.lastTransactionSent = lastTransactionSent;
		},
		setCheckoutStep: (
			state: OrderDomain.State,
			action: PayloadAction<OrderDomain.State>
		) => {
			const { order, checkout, typewriterNote, lastTransactionSent } =
				action.payload;

			state.order = order;
			state.checkout = checkout;
			state.typewriterNote = typewriterNote;
			state.lastTransactionSent = lastTransactionSent;
		},
		setPickupLocation: (
			state: OrderDomain.State,
			action: PayloadAction<OrderDomain.State>
		) => {
			const { pickupLocation } = action.payload.checkout;
			state.checkout.pickupLocation = pickupLocation;
		},
		addGiftCardPayment: (
			state: OrderDomain.State,
			action: PayloadAction<OrderDomain.State>
		) => {
			const { giftCardPayments } = action.payload.checkout;
			const giftAmount = giftCardPayments.map(d => {
				return {
					cardNumber: d.cardNumber,
					amount: Math.round(d.amount)
				};
			});
			state.checkout.giftCardPayments = giftAmount;
		},
		addCreditCardPayment: (
			state: OrderDomain.State,
			action: PayloadAction<OrderDomain.State>
		) => {
			const { creditCardPayment } = action.payload.checkout;
			state.checkout.creditCardPayment = creditCardPayment;
		},
		removeGiftCardPayment: (
			state: OrderDomain.State,
			action: PayloadAction<OrderDomain.State>
		) => {
			const { giftCardPayments } = action.payload.checkout;
			// biggby*** no actions present, hence commented out
			// const state_giftCardPayments = state.checkout.giftCardPayments;
			// const removeAmount = giftCardPayments.filter(
			// 	({ cardNumber }) => cardNumber !== state_giftCardPayments
			// );
			state.checkout.giftCardPayments = giftCardPayments;
		},
		setPickName: (
			state: OrderDomain.State,
			action: PayloadAction<OrderDomain.State>
		) => {
			const { pickupName } = action.payload.checkout;
			state.checkout.pickupName = pickupName;
		},
		setCarMake: (
			state: OrderDomain.State,
			action: PayloadAction<OrderDomain.State>
		) => {
			const carMake = action.payload.checkout.car?.make;
			const resMake = {
				...action.payload.checkout?.car,
				model: carMake
			};
			if (carMake) {
				state.checkout.car = resMake;
			}
		},
		setCarModal: (
			state: OrderDomain.State,
			action: PayloadAction<OrderDomain.State>
		) => {
			const carModel = action.payload.checkout.car?.model;
			const resModel = {
				...action.payload.checkout?.car,
				model: carModel
			};
			if (carModel) {
				state.checkout.car = resModel;
			}
		},
		setCarColor: (
			state: OrderDomain.State,
			action: PayloadAction<OrderDomain.State>
		) => {
			const carColor = action.payload.checkout.car?.color;
			const resModel = {
				...action.payload.checkout?.car,
				color: carColor
			};
			if (carColor) {
				state.checkout.car = resModel;
			}
		},
		setGuest: (
			state: OrderDomain.State,
			action: PayloadAction<OrderDomain.State>
		) => {
			const { guestCheckout } = action.payload.checkout;
			state.checkout.guestCheckout = guestCheckout;
		},
		setEmail: (
			state: OrderDomain.State,
			action: PayloadAction<OrderDomain.State>
		) => {
			const { guestEmail } = action.payload.checkout;
			state.checkout.guestEmail = guestEmail;
		},
		setTime: (
			state: OrderDomain.State,
			action: PayloadAction<OrderDomain.State>
		) => {
			const { time } = action.payload.checkout;
			state.checkout.time = time;
		},
		updateTypewriteNote: (
			state: OrderDomain.State,
			action: PayloadAction<OrderDomain.State>
		) => {
			const { typewriterNote } = action.payload;
			state.typewriterNote = typewriterNote;
		}
	}
});

// export const orderState = (state: RootStates) => state.orderReducer;

export const orderReducer = slice.reducer;
export const { sendTransaction } = slice.actions;
